import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import {
  daysInMonth,
  monthLabelsForLanguage,
  parseIsoDateParts,
  toIsoDate,
} from '../utils/birthDateIso';
import styles from './BirthDateSplitPicker.module.css';

type BirthDateSplitPickerProps = {
  id: string;
  value: string;
  onChange: (next: string) => void;
  mandatory?: boolean;
  disabled?: boolean;
  theme?: 'light' | 'dark';
  showFieldLabel?: boolean;
};

function MandatoryAsterisk({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }
  return <span aria-hidden="true"> *</span>;
}

/** Day / month / year selects committing `YYYY-MM-DD` when all three are set. */
export function BirthDateSplitPicker({
  id,
  value,
  onChange,
  mandatory = false,
  disabled = false,
  theme = 'light',
  showFieldLabel = true,
}: BirthDateSplitPickerProps) {
  const { t, language } = useI18n();
  const monthLabels = useMemo(() => monthLabelsForLanguage(language), [language]);
  const [y, setY] = useState(() => {
    const p = parseIsoDateParts(value);
    return p ? String(p.y) : '';
  });
  const [m, setM] = useState(() => {
    const p = parseIsoDateParts(value);
    return p ? String(p.m) : '';
  });
  const [d, setD] = useState(() => {
    const p = parseIsoDateParts(value);
    return p ? String(p.d) : '';
  });

  useEffect(() => {
    const p = parseIsoDateParts(value);
    if (p) {
      setY(String(p.y));
      setM(String(p.m));
      setD(String(p.d));
    } else if (!value.trim()) {
      setY('');
      setM('');
      setD('');
    }
  }, [value]);

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 120;
  const maxYear = currentYear;

  const yi = y ? Number(y) : NaN;
  const mi = m ? Number(m) : NaN;
  const yearOk = Number.isFinite(yi) && yi >= minYear && yi <= maxYear;
  const monthOk = Number.isFinite(mi) && mi >= 1 && mi <= 12;
  const maxDay = yearOk && monthOk ? daysInMonth(yi, mi) : 31;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let yr = maxYear; yr >= minYear; yr -= 1) {
      list.push(yr);
    }
    return list;
  }, [maxYear, minYear]);

  const days = useMemo(() => {
    const n = yearOk && monthOk ? maxDay : 31;
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [yearOk, monthOk, maxDay]);

  const commit = (ny: string, nm: string, nd: string) => {
    setY(ny);
    setM(nm);
    setD(nd);
    if (ny && nm && nd) {
      let yi2 = Number(ny);
      let mi2 = Number(nm);
      let di2 = Number(nd);
      const cap = daysInMonth(yi2, mi2);
      if (di2 > cap) {
        di2 = cap;
        setD(String(di2));
      }
      onChange(toIsoDate(yi2, mi2, di2));
    } else {
      onChange('');
    }
  };

  const wrapClass = [styles.wrap, theme === 'dark' ? styles.wrapDark : ''].filter(Boolean).join(' ');

  return (
    <div className={wrapClass}>
      {showFieldLabel ? (
        <div className={styles.label} id={`${id}-legend`}>
          {t('profile.field.dateOfBirth')}
          <MandatoryAsterisk visible={mandatory} />
        </div>
      ) : null}
      <div
        className={styles.row}
        role="group"
        aria-labelledby={showFieldLabel ? `${id}-legend` : undefined}
      >
        <div className={styles.part}>
          <label className={styles.partLabel} htmlFor={`${id}-day`}>
            {t('common.day')}
          </label>
          <select
            id={`${id}-day`}
            className={styles.select}
            value={d}
            disabled={disabled}
            aria-required={mandatory || undefined}
            onChange={(event) => commit(y, m, event.target.value)}
          >
            <option value="">{t('common.day')}</option>
            {days.map((dayNum) => (
              <option key={dayNum} value={String(dayNum)}>
                {dayNum}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.part}>
          <label className={styles.partLabel} htmlFor={`${id}-month`}>
            {t('common.month')}
          </label>
          <select
            id={`${id}-month`}
            className={styles.select}
            value={m}
            disabled={disabled}
            aria-required={mandatory || undefined}
            onChange={(event) => {
              const nm = event.target.value;
              let nd = d;
              if (nm && y && d) {
                const yiN = Number(y);
                const miN = Number(nm);
                const diN = Number(d);
                const cap = daysInMonth(yiN, miN);
                if (diN > cap) {
                  nd = String(cap);
                }
              }
              commit(y, nm, nd);
            }}
          >
            <option value="">{t('common.month')}</option>
            {monthLabels.map((label, index) => (
              <option key={label} value={String(index + 1)}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.part}>
          <label className={styles.partLabel} htmlFor={`${id}-year`}>
            {t('common.year')}
          </label>
          <select
            id={`${id}-year`}
            className={styles.select}
            value={y}
            disabled={disabled}
            aria-required={mandatory || undefined}
            onChange={(event) => {
              const ny = event.target.value;
              let nd2 = d;
              if (ny && m && d) {
                const yiN = Number(ny);
                const miN = Number(m);
                const diN = Number(d);
                const cap = daysInMonth(yiN, miN);
                if (diN > cap) {
                  nd2 = String(cap);
                }
              }
              commit(ny, m, nd2);
            }}
          >
            <option value="">{t('common.year')}</option>
            {years.map((yr) => (
              <option key={yr} value={String(yr)}>
                {yr}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
